import React, { useState } from "react";
import { Button, Table } from "react-bootstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

// RTK Query hooks – adjust paths as needed
import { useGetStocksQuery } from "../../slices/stocksApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetTransfersQuery } from "../../slices/transfersApiSlice";
import { useGetProductsQuery } from "../../slices/productsApiSlice";

const UseStockAnalysis = () => {
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [firstDate, setFirstDate] = useState("");
  const [secondDate, setSecondDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [report, setReport] = useState([]);

  // Fetch data
  const { data: stocksData = { stocks: [] } } = useGetStocksQuery();
  const { data: suppliesData = { supplies: [] } } = useGetSuppliesQuery();
  const { data: salesData = { sales: [] } } = useGetSalesQuery();
  const { data: transfersData = { transfers: [] } } = useGetTransfersQuery();
  
  const [pageNumber] = useState(1);
  const { data: productsData = { products: [] } } = useGetProductsQuery({ pageNumber });
  
  // Build the per-product report
  const calculateReport = () => {
    if (!firstDate || !secondDate) {
      toast.error("Please select both Start Date and End Date");
      return;
    }
    const start = new Date(firstDate);
    const end = new Date(secondDate);
    const productMap = {};

    const ensureProduct = (name) => {
      if (!productMap[name]) {
        productMap[name] = {
          name,
          initialStock: 0,
          purchases: 0,
          salesQty: 0,
          transfersQty: 0,
          closingStock: 0,
        };
      }
      return productMap[name];
    };

    // 1) INITIAL STOCK on firstDate
    stocksData.stocks.forEach((stock) => {
      const d = new Date(stock.date).toISOString().split("T")[0];
      if (d === firstDate) {
        stock.stockItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.initialStock += item.qty;
        });
      }
    });

    // 2) PURCHASES in [firstDate, secondDate]
    suppliesData.supplies.forEach((sup) => {
      const d = new Date(sup.date);
      if (d >= start && d < end) {
        sup.supplyItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.purchases += item.qty;
        });
      }
    });

    // 3) SALES in [firstDate, secondDate]
    salesData.sales.forEach((sale) => {
      const d = new Date(sale.date);
      if (d >= start && d < end) {
        sale.saleItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.salesQty += item.qty;
        });
      }
    });

    // 4) TRANSFERS in [firstDate, secondDate]
    transfersData.transfers.forEach((trans) => {
      const d = new Date(trans.date);
      if (d >= start && d < end) {
        trans.transferItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.transfersQty += item.qty;
        });
      }
    });

    // 5) CLOSING STOCK on secondDate (or fallback to latest ≤ secondDate)
    const exact = stocksData.stocks.filter(
      (stock) =>
        new Date(stock.date).toISOString().split("T")[0] === secondDate
    );
    if (exact.length) {
      exact.forEach((stock) =>
        stock.stockItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.closingStock += item.qty;
        })
      );
    } else {
      // fallback
      let latest = null;
      stocksData.stocks.forEach((stock) => {
        const d = new Date(stock.date);
        if (d <= end && (!latest || d > latest)) latest = d;
      });
      if (latest) {
        stocksData.stocks
          .filter(
            (stock) => new Date(stock.date).getTime() === latest.getTime()
          )
          .forEach((stock) =>
            stock.stockItems.forEach((item) => {
              const p = ensureProduct(item.name);
              p.closingStock += item.qty;
            })
          );
      }
    }

    // 6) Derived fields: price, values, cost & profit
    const out = Object.values(productMap).map((p) => {
      const prod = productsData.products.find((x) => x.name === p.name);
      p.price = prod?.price || 0;
      p.stockValue = p.closingStock * p.price;
      p.balanceQty =
        p.initialStock + p.purchases - p.salesQty - p.transfersQty;
      p.balanceValue = p.balanceQty * p.price;
      p.difference = p.closingStock - p.balanceQty;
      p.diffValue = p.difference * p.price;
      // average cost from supplies
      const totalCost = suppliesData.supplies
        .flatMap((s) => s.supplyItems)
        .filter((i) => i.name === p.name)
        .reduce((sum, i) => sum + i.itemCost, 0);
      const totalQty = p.initialStock + p.purchases;
      p.averageCost = totalQty > 0 ? totalCost / totalQty : 0;
      p.totalCost = p.averageCost * totalQty;

      // profit = revenue – cost
      p.profit = p.salesQty * p.price - p.totalCost;
      return p;
    });

    setReport(out);
  };

  // Grand totals (over the entire report)
  const getGrandTotal = (field) =>
    report.reduce((sum, p) => sum + (p[field] || 0), 0).toFixed(2);

  // Filter & sort for display/export
  let filtered = report
    .filter((p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((p) => {
  switch (filter) {
    case "positive":
      return p.difference > 0;
    case "negative":
      return p.difference < 0;
    case "lowBalance":
      return p.balanceQty < 10;
    case "topPositive":
      return p.difference > 0;
    case "topNegative":
      return p.difference < 0;
    case "opening":
      return p.initialStock > 0;
    case "purchase":
      return p.purchases > 0;
    case "counted":
      return p.closingStock > 0;
    default:
      return true;
  }
});

  if (filter === "topPositive") {
  filtered = filtered
    .sort((a, b) => b.difference - a.difference)
    .slice(0, 10);
} else if (filter === "topNegative") {
  filtered = filtered
    .sort((a, b) => a.difference - b.difference)
    .slice(0, 10);
} else if (filter === "opening") {
  filtered = filtered
    .filter((p) => p.initialStock > 0)
    .sort((a, b) => b.initialStock - a.initialStock)
    .slice(0, 2500);
} else if (filter === "purchase") {
  filtered = filtered
    .filter((p) => p.purchases > 0)
    .sort((a, b) => b.purchases - a.purchases)
    .slice(0, 2500);
} else if (filter === "counted") {
  filtered = filtered
    .filter((p) => p.closingStock > 0)
    .sort((a, b) => b.closingStock - a.closingStock)
    .slice(0, 2500);
}

  // Export to Excel
  const exportToExcel = () => {
    const rows = filtered.map((p) => ({
      Name: p.name,
      Price: p.price.toFixed(2),
      "Initial Stock": p.initialStock,
      Purchases: p.purchases,
      Sales: p.salesQty,
      Transfers: p.transfersQty,
      "Closing Stock": p.closingStock,
      "Balance Qty": p.balanceQty,
      "Stock Value": p.stockValue.toFixed(2),
      "Balance Value": p.balanceValue.toFixed(2),
      "Avg Cost": p.averageCost.toFixed(2),
      "Total Cost": p.totalCost.toFixed(2),
      Profit: p.profit.toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Analysis");
    XLSX.writeFile(wb, "Stock_Analysis_Report.xlsx");
  };

  return (
    <div className="stock-analysis">
      <h1>Stock Analysis</h1>

      {/* Search */}
      <div className="search-box mb-3">
        <input
          type="text"
          placeholder="Search products..."
          className="form-control"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Date Range */}
      <div className="date-range mb-3">
        <label className="me-3">
          Start:
          <input
            type="date"
            className="form-control d-inline-block w-auto ms-2"
            value={firstDate}
            onChange={(e) => setFirstDate(e.target.value)}
          />
        </label>
        <label>
          End:
          <input
            type="date"
            className="form-control d-inline-block w-auto ms-2"
            value={secondDate}
            onChange={(e) => setSecondDate(e.target.value)}
          />
        </label>
      </div>

      {/* Filters */}
<div className="filters mb-3">
  <Button variant="outline-secondary" onClick={() => setFilter("all")}>
    All
  </Button>
  <Button variant="outline-success" onClick={() => setFilter("positive")}>
    Positive
  </Button>
  <Button variant="outline-danger" onClick={() => setFilter("negative")}>
    Negative
  </Button>
  <Button variant="outline-primary" onClick={() => setFilter("topPositive")}>
    Top +ve
  </Button>
  <Button variant="outline-warning" onClick={() => setFilter("topNegative")}>
    Top –ve
  </Button>
  <Button variant="outline-info" onClick={() => setFilter("lowBalance")}>
    Low Bal.
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("opening")}>
    Opening
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("purchase")}>
    Purchase
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("counted")}>
    Counted
  </Button>
</div>
      {/* Actions */}
      <div className="mb-3">
        <Button variant="primary" className="me-2" onClick={calculateReport}>
          Generate Report
        </Button>
        <Button style={{display: "none"}} variant="success" onClick={exportToExcel}>
          Export to Excel
        </Button>
      </div>

      {/* Summary */}
      {report.length > 0 && (
        <div style={{display: "none"}} className="summary mb-4" >
          <h5>Total Stock Value: ${getGrandTotal("stockValue")}</h5>
          <h5>Total Balance Value: ${getGrandTotal("balanceValue")}</h5>
          <h5>Total Difference Value: ${getGrandTotal("diffValue")}</h5>
          <h5>Total Cost: ${getGrandTotal("totalCost")}</h5>
          <h5>Total Profit: ${getGrandTotal("profit")}</h5>
        </div>
      )}

      {/* Data Table */}
      {filtered.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Initial Stock</th>
              <th>Purchases</th>
              <th>Sales</th>
              <th>Transfers</th>
              <th>Balance Qty</th>
              <th>Closing Stock</th>
              <th>Diff</th>
              <th>Diff Value</th>
              <th>Stock Value</th>
              <th>Balance Value</th>
              <th>Avg Cost</th>
              <th>Total Cost</th>
              <th>Profit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td>${p.price.toFixed(2)}</td>
                <td>{p.initialStock}</td>
                <td>{p.purchases}</td>
                <td>{p.salesQty}</td>
                <td>{p.transfersQty}</td>
                <td>{p.balanceQty}</td>
                <td>{p.closingStock}</td>
                <td>{p.difference}</td>
                <td>{p.diffValue}</td>
                <td>${p.stockValue.toFixed(2)}</td>
                <td>${p.balanceValue.toFixed(2)}</td>
                <td>${p.averageCost.toFixed(2)}</td>
                <td>${p.totalCost.toFixed(2)}</td>
                <td>${p.profit.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default UseStockAnalysis;